import {
  ActivityTemplate,
  ActivityTemplateCreateRequest,
  ActivityTemplateTagCreate,
  ActivityTemplateUpdate,
  AddActivityDocumentTemplate,
  CompletionCriterionTemplateUpdate,
  UpdateActivityDocumentTemplate,
  ProjectMapTemplate,
} from '@beeboard/common';
import {
  checkTemplateAddApproverToActivity,
  checkTemplateAddCompletionCriterionToActivityAtIndex,
  checkTemplateAddDocumentToActivity,
  checkTemplateAddLinkToActivity,
  checkTemplateAddPredecessorToActivity,
  checkTemplateAddSuccessorToActivity,
  checkTemplateAddTagToActivity,
  checkTemplateAddWatcherToActivity,
  checkTemplateRemoveActivity,
  checkTemplateRemoveApproverFromActivity,
  checkTemplateRemoveCCFromActivity,
  checkTemplateRemoveDocumentFromActivity,
  checkTemplateRemoveLinkFromActivity,
  checkTemplateRemoveTagFromActivity,
  checkTemplateUpdateActivity,
  checkTemplateUpdateActivityDocument,
  checkTemplateRemovePredecessorFromActivity,
  checkTemplateRemoveSuccessorFromActivity,
  checkTemplateUpdateCC,
  isUserTemplateEditorOrAbove,
  checkTemplateAddActivity,
  checkTemplateRemoveWatcherFromActivity,
} from '@beeboard/common';
import { defineStore } from 'pinia';
import { useActivityTemplateStore } from './activityTemplateStore';
import { useProfileStore } from './profileStore';
import { useTemplateStore } from './templateStore';

export const useActivityTemplateRulesStore = defineStore(
  'activityTemplateRulesStore',
  {
    getters: {
      user() {
        return useProfileStore().profile;
      },
      template() {
        return (templateId: string): ProjectMapTemplate => {
          const template = useTemplateStore().template(templateId);

          return template;
        };
      },
      editorsTemplate() {
        return (templateId: string) => {
          const template = useTemplateStore().editorsTemplate(templateId);

          return template;
        };
      },
      activitySummary() {
        return (templateId: string, activityId: string) => {
          const activity = useTemplateStore().activitySummary(
            templateId,
            activityId,
          );

          return activity as ActivityTemplate;
        };
      },
      // A summary lists its predecessors only; its successors are the
      // activities that list it as theirs. A move checks both (WI #5795).
      activitySummaryWithSuccessors() {
        return (templateId: string, activityId: string) => {
          const successors = this.template(templateId)
            .activities.filter(({ predecessors }) =>
              predecessors?.includes(activityId),
            )
            .map(({ id }) => id);

          return {
            ...this.activitySummary(templateId, activityId),
            successors,
          } as ActivityTemplate;
        };
      },
      hasActivity() {
        return (templateId: string, activityId: string) => {
          return useActivityTemplateStore().hasActivity(templateId, activityId);
        };
      },
      activity() {
        return (templateId: string, activityId: string) => {
          const activity = useActivityTemplateStore().activity(
            templateId,
            activityId,
          );

          return activity;
        };
      },
    },
    actions: {
      isUserTemplateEditorOrAbove(templateId: string) {
        return !isUserTemplateEditorOrAbove(
          this.user.id,
          this.template(templateId).owner,
          this.editorsTemplate(templateId),
        ).length;
      },
      checkAddActivity(
        templateId: string,
        payload: ActivityTemplateCreateRequest,
      ) {
        return checkTemplateAddActivity(
          this.user.id,
          this.template(templateId),
          payload,
          this.editorsTemplate(templateId),
        );
      },
      /** `activityIds`: the set a multi-move moves together (WI #5795). */
      checkUpdateActivity(
        templateId: string,
        activityId: string,
        updateRequest: ActivityTemplateUpdate,
        activityIds?: string[],
      ) {
        return checkTemplateUpdateActivity(
          this.user.id,
          this.template(templateId),
          updateRequest,
          this.hasActivity(templateId, activityId)
            ? this.activity(templateId, activityId)
            : this.activitySummaryWithSuccessors(templateId, activityId),
          this.editorsTemplate(templateId),
          activityIds,
        );
      },
      checkRemoveActivity(templateId: string, activityId: string) {
        return checkTemplateRemoveActivity(
          this.user.id,
          this.template(templateId),
          this.hasActivity(templateId, activityId)
            ? this.activity(templateId, activityId)
            : this.activitySummary(templateId, activityId),
          this.editorsTemplate(templateId),
        );
      },
      checkAddTagToActivity(
        templateId: string,
        activityId: string,
        createTagRequest: ActivityTemplateTagCreate,
      ) {
        return checkTemplateAddTagToActivity(
          this.user.id,
          this.template(templateId),
          this.activity(templateId, activityId),
          createTagRequest,
          this.editorsTemplate(templateId),
        );
      },
      checkRemoveTagFromActivity(
        templateId: string,
        activityId: string,
        tagId: string,
      ) {
        return checkTemplateRemoveTagFromActivity(
          this.user.id,
          this.template(templateId),
          this.activity(templateId, activityId),
          tagId,
          this.editorsTemplate(templateId),
        );
      },
      checkAddCompletionCriterion(
        templateId: string,
        activityId: string,
        index?: number,
      ) {
        const activity = this.activity(templateId, activityId);

        return checkTemplateAddCompletionCriterionToActivityAtIndex(
          this.user.id,
          this.template(templateId),
          this.activity(templateId, activityId),
          index || activity.completion_criteria.length,
          this.editorsTemplate(templateId),
        );
      },
      checkUpdateCC(
        templateId: string,
        activityId: string,
        criterionId: string,
        CCPayload: CompletionCriterionTemplateUpdate,
      ) {
        return checkTemplateUpdateCC(
          this.user.id,
          this.template(templateId),
          activityId,
          CCPayload,
          criterionId,
          this.editorsTemplate(templateId),
        );
      },
      checkRemoveCC(
        templateId: string,
        activityId: string,
        criterionId: string,
      ) {
        return checkTemplateRemoveCCFromActivity(
          this.user.id,
          this.template(templateId),
          this.activity(templateId, activityId),
          criterionId,
          this.editorsTemplate(templateId),
        );
      },
      checkAddActivityDocument(
        templateId: string,
        activityId: string,
        payload: AddActivityDocumentTemplate,
      ) {
        return checkTemplateAddDocumentToActivity(
          this.user.id,
          this.template(templateId),
          this.activity(templateId, activityId),
          payload,
          this.editorsTemplate(templateId),
        );
      },
      checkUpdateActivityDocument(
        templateId: string,
        activityId: string,
        documentId: string,
        payload: UpdateActivityDocumentTemplate,
      ) {
        return checkTemplateUpdateActivityDocument(
          this.user.id,
          this.template(templateId),
          this.activity(templateId, activityId),
          payload,
          documentId,
          this.editorsTemplate(templateId),
        );
      },
      checkRemoveActivityDocument(
        templateId: string,
        activityId: string,
        documentId: string,
      ) {
        return checkTemplateRemoveDocumentFromActivity(
          this.user.id,
          this.template(templateId),
          this.activity(templateId, activityId),
          documentId,
          this.editorsTemplate(templateId),
        );
      },
      checkAddApprover(
        templateId: string,
        activityId: string,
        approverId: string,
      ) {
        return checkTemplateAddApproverToActivity(
          this.user.id,
          this.template(templateId),
          this.activity(templateId, activityId),
          approverId,
          this.editorsTemplate(templateId),
        );
      },
      checkRemoveApprover(
        templateId: string,
        activityId: string,
        approverId: string,
      ) {
        return checkTemplateRemoveApproverFromActivity(
          this.user.id,
          this.template(templateId),
          this.activity(templateId, activityId),
          approverId,
          this.editorsTemplate(templateId),
        );
      },
      checkAddWatcher(
        templateId: string,
        activityId: string,
        watcherId: string,
      ) {
        return checkTemplateAddWatcherToActivity(
          this.user.id,
          this.template(templateId),
          this.activity(templateId, activityId),
          watcherId,
          this.editorsTemplate(templateId),
        );
      },
      checkRemoveWatcher(
        templateId: string,
        activityId: string,
        watcherId: string,
      ) {
        return checkTemplateRemoveWatcherFromActivity(
          this.user.id,
          this.template(templateId),
          this.activity(templateId, activityId),
          watcherId,
          this.editorsTemplate(templateId),
        );
      },
      checkAddLink(templateId: string, activityId: string, linkId: string) {
        return checkTemplateAddLinkToActivity(
          this.user.id,
          this.template(templateId),
          this.activity(templateId, activityId),
          linkId,
          this.editorsTemplate(templateId),
        );
      },
      checkRemoveLink(templateId: string, activityId: string, linkId: string) {
        return checkTemplateRemoveLinkFromActivity(
          this.user.id,
          this.template(templateId),
          this.activity(templateId, activityId),
          linkId,
          this.editorsTemplate(templateId),
        );
      },
      checkAddPredecessor(
        templateId: string,
        predecessorId: string,
        successorId: string,
      ) {
        return checkTemplateAddPredecessorToActivity(
          this.user.id,
          this.template(templateId),
          predecessorId,
          this.activitySummary(templateId, successorId),
          this.editorsTemplate(templateId),
        );
      },
      checkRemovePredecessor(
        templateId: string,
        predecessorId: string,
        successorId: string,
      ) {
        return checkTemplateRemovePredecessorFromActivity(
          this.user.id,
          this.template(templateId),
          predecessorId,
          this.activitySummary(templateId, successorId),
          this.editorsTemplate(templateId),
        );
      },
      checkAddSuccessor(
        templateId: string,
        predecessorId: string,
        successorId: string,
      ) {
        return checkTemplateAddSuccessorToActivity(
          this.user.id,
          this.template(templateId),
          this.activity(templateId, predecessorId),
          successorId,
          this.editorsTemplate(templateId),
        );
      },
      checkRemoveSuccessor(
        templateId: string,
        predecessorId: string,
        successorId: string,
      ) {
        return checkTemplateRemoveSuccessorFromActivity(
          this.user.id,
          this.template(templateId),
          this.activity(templateId, predecessorId),
          successorId,
          this.editorsTemplate(templateId),
        );
      },
    },
  },
);
